import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaSun, FaMoon, FaBars, FaTimes } from "react-icons/fa";
import "./Sidebar.css";

function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [sidebarOpen, setSidebarOpen]         = useState(false);
  const [darkMode, setDarkMode]               = useState(() => {
    return localStorage.getItem("darkMode") === "true";
  });

  // ✅ Store admin in state so the sidebar re-renders when it changes
  const [adminData, setAdminData] = useState(() =>
    JSON.parse(localStorage.getItem("admin"))
  );

  const BASE_URL = "https://api.kemari-corp.com/";

  // ✅ Listen for the custom "adminUpdated" event fired by AdminUsers
  //    after the logged-in admin edits their own profile
  useEffect(() => {
    const handleAdminUpdate = () => {
      setAdminData(JSON.parse(localStorage.getItem("admin")));
    };
    window.addEventListener("adminUpdated", handleAdminUpdate);
    return () => window.removeEventListener("adminUpdated", handleAdminUpdate);
  }, []);

  // ── Apply / remove dark class on body ──
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("dark");
    } else {
      document.body.classList.remove("dark");
    }
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  // ── Close sidebar on route change (mobile) ──
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // ── Prevent body scroll when sidebar is open on mobile ──
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  const menuItems = [
    { name: "Dashboard",        path: "/admin/dashboard" },
    { name: "Messages",         path: "/admin/messages" },
    { name: "Service Requests", path: "/admin/service-requests" },
    { name: "Calendar",         path: "/admin/calendar" },
    { name: "Admin Users",      path: "/admin/admin-users" },
  ];

  const handleLogout = () => {
    localStorage.removeItem("admin");
    navigate("/admin/login", { replace: true });
  };

  return (
    <>
      {/* ── Mobile Top Bar ── */}
      <div className="sb-mobile-bar">
        <button
          className="sb-hamburger"
          onClick={() => setSidebarOpen(true)}
          aria-label="Open menu"
        >
          <FaBars size={18} />
        </button>

        <button
          className="sb-mobile-darkmode"
          onClick={() => setDarkMode(!darkMode)}
        >
          {darkMode ? <FaSun size={15} /> : <FaMoon size={15} />}
        </button>
      </div>

      {/* ── Backdrop overlay (mobile) ── */}
      {sidebarOpen && (
        <div
          className="sb-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <div className={`sidebar ${sidebarOpen ? "open" : ""}`}>

        {/* Close button — mobile only */}
        <button
          className="sb-close-btn"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close menu"
        >
          <FaTimes size={16} />
        </button>

        {/* ── Profile Section ── */}
        <div className="sb-profile">
          <div className="sb-profile-img-wrap">
            {adminData?.profile_image ? (
              <img
                src={`${BASE_URL}${adminData.profile_image}`}
                alt={adminData.username}
                className="sb-profile-img"
              />
            ) : (
              <div className="sb-profile-avatar-placeholder">
                {adminData?.username?.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <p className="sb-profile-name">{adminData?.username || "Admin"}</p>
          <p className="sb-profile-role">{adminData?.role || "admin"}</p>
        </div>

        <div className="sb-divider" />

        {/* ── Navigation ── */}
        <nav className="sb-nav">
          {menuItems.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              className={`sb-nav-link ${location.pathname === item.path ? "active" : ""}`}
            >
              {item.name}
            </Link>
          ))}
        </nav>

        {/* ── Dark Mode Toggle ── */}
        <button
          className="sb-darkmode-btn"
          onClick={() => setDarkMode(!darkMode)}
          title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
        >
          <span className="sb-darkmode-icon">
            {darkMode ? <FaSun size={14} /> : <FaMoon size={14} />}
          </span>
          <span className="sb-darkmode-text">
            {darkMode ? "Light Mode" : "Dark Mode"}
          </span>
        </button>

        <div className="sb-spacer" />

        {/* ── Logout Button ── */}
        <button
          className="sb-logout-btn"
          onClick={() => setShowLogoutModal(true)}
        >
          <span className="sb-logout-text">Logout</span>
        </button>

      </div>

      {/* ── Logout Modal ── */}
      {showLogoutModal && (
        <div className="sb-modal-overlay">
          <div className="sb-modal">
            <h3 className="sb-modal-title">Confirm Logout</h3>
            <p className="sb-modal-body">Are you sure you want to log out?</p>
            <div className="sb-modal-actions">
              <button
                className="sb-modal-btn sb-modal-cancel"
                onClick={() => setShowLogoutModal(false)}
              >
                Cancel
              </button>
              <button
                className="sb-modal-btn sb-modal-confirm"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sidebar;